import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  LayoutGrid,
  List as ListIcon,
  Loader2,
  Pencil,
  Search,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  BUILT_IN_FILTERS,
  CATALOG_SORTS,
  useCatalogFilters,
  useCatalogSearch,
  useUpdateCatalogItem,
  type CatalogProduct,
  type CatalogSearchModel,
} from './catalog-search.api';

/**
 * Product search optimisation — the terms a customer can find an item by.
 *
 * The page is a catalogue search with one thing to edit per row: an item's
 * search tags. Everything else on it exists to *find* the row worth editing,
 * which is why the filters, the sort and the two views are all in front of the
 * reader rather than behind a menu.
 */

export function SearchOptimizationPage() {
  const { t } = useTranslation();

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [sort, setSort] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [view, setView] = useState<'table' | 'grid'>('table');
  const [editing, setEditing] = useState<CatalogProduct | null>(null);

  const businessFilters = useCatalogFilters();

  const model: CatalogSearchModel = useMemo(
    () => ({ Query: query, Page: page, Size: size, Filters: selected, Sort: sort }),
    [query, page, size, selected, sort]
  );

  const search = useCatalogSearch(model);
  const products = search.data?.items ?? [];
  // A new page, sort or filter keeps the previous answer on screen while the
  // next one arrives; this is what says it is being replaced.
  const busy = search.isFetching && !search.isPending;
  const total = search.data?.count ?? 0;

  /** Built-in tags first, then whatever the business added. */
  const filters = useMemo(
    () => [
      ...BUILT_IN_FILTERS.map((tag) => ({
        key: tag,
        label: t(`searchOptimization.filter.${tag}` as const),
      })),
      ...(businessFilters.data ?? []).map((filter) => ({
        key: filter.tag || filter.id,
        label: filter.name ?? '—',
      })),
    ],
    [businessFilters.data, t]
  );

  function toggleFilter(key: string) {
    setPage(0);
    setSelected((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('searchOptimization.title')}
        description={t('searchOptimization.subtitle')}
      />

      {/* ------------------------------------------------------ filters */}
      {/* Locked while a search is in flight, as the shared filter bar is. */}
      <fieldset disabled={busy} className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {businessFilters.isPending ? (
            <Skeleton className="h-6 w-64" />
          ) : (
            <>
              {filters.map((filter) => {
                const active = selected.includes(filter.key);
                return (
                  <button
                    key={filter.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleFilter(filter.key)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs transition-colors disabled:pointer-events-none disabled:opacity-50',
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    )}
                  >
                    {filter.label}
                  </button>
                );
              })}
              {/* Only offered when it would do something — the original showed
                  "Clear Filters" permanently, beside nothing to clear. */}
              {selected.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelected([]);
                    setPage(0);
                  }}
                >
                  {t('searchOptimization.clearFilters')}
                </Button>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <form
            role="search"
            className="flex flex-1 gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void search.refetch();
              setQuery(draft);
              setPage(0);
            }}
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t('searchOptimization.searchPlaceholder')}
                aria-label={t('searchOptimization.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>

          <div className="flex gap-2">
            <Select
              value={String(sort)}
              onValueChange={(value) => {
                setSort(Number(value));
                setPage(0);
              }}
            >
              <SelectTrigger className="h-10 flex-1 sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATALOG_SORTS.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex shrink-0 rounded-lg border p-0.5">
              <Button
                variant={view === 'table' ? 'secondary' : 'ghost'}
                size="icon-sm"
                aria-label={t('searchOptimization.tableView')}
                aria-pressed={view === 'table'}
                onClick={() => setView('table')}
              >
                <ListIcon className="size-4" />
              </Button>
              <Button
                variant={view === 'grid' ? 'secondary' : 'ghost'}
                size="icon-sm"
                aria-label={t('searchOptimization.gridView')}
                aria-pressed={view === 'grid'}
                onClick={() => setView('grid')}
              >
                <LayoutGrid className="size-4" />
              </Button>
            </div>
          </div>
        </div>

        {search.isSuccess && (
          <p className="text-muted-foreground text-sm">
            {t('searchOptimization.showingOf', {
              shown: products.length,
              total,
            })}
          </p>
        )}
      </fieldset>

      {search.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('searchOptimization.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void search.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {search.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-12 shrink-0 rounded" />
              <Skeleton className="h-4 w-64" />
            </div>
          ))}
        </div>
      )}

      {search.isSuccess && products.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('searchOptimization.noResults')}
          </p>
        </div>
      )}

      {products.length > 0 && (
        <div className="space-y-3">
          <div className="relative rounded-xl">
            <BusyOverlay show={busy} />
            <div className={cn(busy && 'opacity-60 transition-opacity')}>
              {view === 'table' ? (
                <div className="divide-y rounded-xl border">
                  {products.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col gap-3 p-3 sm:flex-row sm:items-start"
                    >
                      {item.itemImage ? (
                        <img
                          src={item.itemImage}
                          alt=""
                          className="bg-muted size-14 shrink-0 rounded object-contain"
                        />
                      ) : (
                        <span className="bg-muted size-14 shrink-0 rounded" />
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">
                          {[item.brand, item.name].filter(Boolean).join(' ')}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {item.description}
                        </p>
                        <p className="text-muted-foreground font-mono text-xs">
                          {[item.gtin, item.internalId].filter(Boolean).join(' · ')}
                        </p>
                      </div>

                      <div className="min-w-0 sm:w-64">
                        <p className="text-muted-foreground text-xs">
                          {t('searchOptimization.searchTags')}
                        </p>
                        {item.searchTags ? (
                          <div className="flex items-start gap-1">
                            <p className="min-w-0 text-xs break-words">
                              {item.searchTags}
                            </p>
                            <CopyButton value={item.searchTags} />
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-xs">—</p>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => setEditing(item)}
                      >
                        <Pencil className="size-3.5" />
                        {t('searchOptimization.edit')}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                  {products.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setEditing(item)}
                      className="bg-card hover:border-primary/50 flex flex-col gap-2 rounded-xl border p-3 text-left transition-colors"
                    >
                      <p className="text-muted-foreground truncate font-mono text-xs">
                        {item.gtin}
                      </p>
                      {item.itemImage ? (
                        <img
                          src={item.itemImage}
                          alt=""
                          className="bg-muted h-20 w-full rounded object-contain"
                        />
                      ) : (
                        <span className="bg-muted h-20 w-full rounded" />
                      )}
                      <div className="min-w-0">
                        <p className="text-muted-foreground truncate text-xs">
                          {item.brand}
                        </p>
                        <p className="truncate text-sm">{item.name}</p>
                        <p className="text-muted-foreground truncate text-xs">
                          {item.description}
                        </p>
                      </div>
                      {/* A tagged item is the point of the page, so say which. */}
                      {item.searchTags && (
                        <Badge variant="secondary">
                          {t('searchOptimization.tagged')}
                        </Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}

      <EditTagsSheet
        item={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />
    </div>
  );
}

/** The one thing this page edits. */
function EditTagsSheet({
  item,
  open,
  onOpenChange,
}: {
  item: CatalogProduct | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateCatalogItem();

  const [tags, setTags] = useState('');
  const [hydrated, setHydrated] = useState<string | null>(null);

  if (item && hydrated !== item.id) {
    setHydrated(item.id);
    setTags(item.searchTags ?? '');
  }

  if (!item) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{t('searchOptimization.editItem')}</SheetTitle>
        </SheetHeader>

        <div className="space-y-4 p-4">
          <div className="flex items-start gap-3 rounded-xl border p-3">
            {item.itemImage ? (
              <img
                src={item.itemImage}
                alt=""
                className="bg-muted size-16 shrink-0 rounded object-contain"
              />
            ) : (
              <span className="bg-muted size-16 shrink-0 rounded" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {[item.brand, item.name].filter(Boolean).join(' ')}
              </p>
              <p className="text-muted-foreground text-xs">
                {item.description}
              </p>
              <p className="text-muted-foreground font-mono text-xs">
                {item.gtin}
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('searchOptimization.customSearchTag')}
            </Label>
            {/* A textarea, not a single line: these run to a paragraph of
                misspellings and synonyms, and the input showed one word of it. */}
            <Textarea
              rows={4}
              value={tags}
              placeholder={t('searchOptimization.customSearchTag')}
              onChange={(event) => setTags(event.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              {t('searchOptimization.customSearchTagNote')}
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              disabled={update.isPending}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  { ...item, searchTags: tags },
                  {
                    onSuccess: () => {
                      toast.success(t('searchOptimization.saved'));
                      onOpenChange(false);
                    },
                    onError: () =>
                      toast.error(t('searchOptimization.actionFailed')),
                  }
                )
              }
            >
              {update.isPending && <Loader2 className="animate-spin" />}
              {t('common.save')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
